import { useCallback, useEffect, useRef, useState } from "react";
import { AIDE_WS_BASE, AIDE_WS_PATH } from "./env";
import type {
  ConnectionStatus,
  EnvelopeFromClient,
  EnvelopeFromServer,
} from "./aide-protocol";

interface UseAideSocketOptions {
  consumerId: string;
  sessionId?: string;
  // Called for every server envelope. Use this to update message lists,
  // route notifications, etc.
  onEnvelope?: (envelope: EnvelopeFromServer) => void;
  // Called once on the welcome envelope.
  onWelcome?: (info: { sessionId: string; consumerId: string }) => void;
}

interface UseAideSocketResult {
  status: ConnectionStatus;
  sessionId: string | null;
  lastError: string | null;
  send: (envelope: EnvelopeFromClient) => void;
}

/**
 * Single-connection hook for the Aide WebSocket. Reconnects with
 * backoff (2s, 4s, 8s, capped at 30s) on transient close. The hook
 * owns the socket; render code consumes status + envelopes via the
 * onEnvelope callback.
 *
 * Per LS-SPEC-22 §Wire Protocol — every envelope is a JSON object with
 * a top-level `type`. The hook stays pure transport; envelope shape
 * lives in aide-protocol.ts.
 */
export function useAideSocket(options: UseAideSocketOptions): UseAideSocketResult {
  const { consumerId, sessionId: initialSessionId, onEnvelope, onWelcome } = options;
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [lastError, setLastError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(
    initialSessionId ?? null,
  );

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const closedExplicitlyRef = useRef(false);

  const handleEnvelopeRef = useRef(onEnvelope);
  const handleWelcomeRef = useRef(onWelcome);
  useEffect(() => {
    handleEnvelopeRef.current = onEnvelope;
    handleWelcomeRef.current = onWelcome;
  }, [onEnvelope, onWelcome]);

  const connect = useCallback(() => {
    if (!consumerId) return;
    closedExplicitlyRef.current = false;
    setStatus("connecting");

    const params = new URLSearchParams({ consumer_id: consumerId });
    if (sessionId) params.set("session_id", sessionId);
    const url = `${AIDE_WS_BASE}${AIDE_WS_PATH}?${params.toString()}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setStatus("open");
      setLastError(null);
    };

    ws.onmessage = (event) => {
      let envelope: EnvelopeFromServer;
      try {
        envelope = JSON.parse(event.data as string);
      } catch (err) {
        setLastError(`malformed envelope: ${(err as Error).message}`);
        return;
      }
      if (envelope.type === "welcome") {
        setSessionId(envelope.session_id);
        handleWelcomeRef.current?.({
          sessionId: envelope.session_id,
          consumerId: envelope.consumer_id,
        });
      }
      if (envelope.type === "error") {
        setLastError(envelope.detail);
      }
      handleEnvelopeRef.current?.(envelope);
    };

    ws.onerror = () => {
      setStatus("error");
      setLastError("socket error");
    };

    ws.onclose = () => {
      wsRef.current = null;
      setStatus("closed");
      if (closedExplicitlyRef.current) return;
      // Exponential backoff.
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const delayMs = Math.min(2000 * Math.pow(2, attempt - 1), 30_000);
      setTimeout(() => {
        if (!closedExplicitlyRef.current) connect();
      }, delayMs);
    };
  }, [consumerId, sessionId]);

  useEffect(() => {
    connect();
    return () => {
      closedExplicitlyRef.current = true;
      wsRef.current?.close();
    };
    // We deliberately depend only on consumerId — re-connecting on
    // every sessionId state change would loop because the server sets
    // sessionId from welcome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerId]);

  const send = useCallback((envelope: EnvelopeFromClient) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setLastError("not connected");
      return;
    }
    ws.send(JSON.stringify(envelope));
  }, []);

  return { status, sessionId, lastError, send };
}
